import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, catchError } from 'rxjs/operators';
import { Observable, EMPTY, iif, of } from 'rxjs';
import { Action } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';

import { AutocompletePlacesService } from '@search-bar/services/autocomplete-places.service';
import { SearchType } from '@search-bar/entities/search-type';
import {
  searchDestinationPlaceInfoAction, searchDestinationPlaceInfoSuccessAction, searchDestinationPlacesAction,
  searchDestinationPlacesSuccessAction, searchExtraByQueryAction, searchExtraByQuerySuccessAction,
  searchPackageDestinationPlaceInfoSuccessAction, searchPlacesAction, searchPlacesSuccessAction,
  searchTravelExtraActivityInfoAction, selectTravelExtraInputAction
} from '@shared/base-search-bar/store/actions/search-bar.actions';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { clearHotelResultsAction } from '@hotels/hotel-results/store/actions/hotels.actions';
import { ToastrTypes } from '@app/entities/toastr';
import { AppFacade } from '@app/app.facade';
import { ActivitySearchCodeType } from '@activities/entities/activities-acutocomplete';
import { cleanActivitiesResultsAction } from '@activities/activities-results/store/actions/activities-results.actions';
import { NavigationService } from '@app/services/navigation.service';

@Injectable()
export class PlacesEffects {

  constructor(
    private readonly action$: Actions,
    private readonly appFacade: AppFacade,
    private readonly service: AutocompletePlacesService,
    private readonly navigationService: NavigationService,
    private readonly translateService: TranslocoService,
    private readonly baseSearchBarFacade: BaseSearchBarFacade
  ) { }

  searchPlacesAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(searchPlacesAction),
      switchMap(action => this.service.searchPlaces(action.term).pipe(
        map(places => searchPlacesSuccessAction({ places: places }))
      )),
      catchError(() => EMPTY)
    ));

  searchDestinationPlaces$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(searchDestinationPlacesAction),
      switchMap(action => this.service.searchDestinationPlaces(action.query).pipe(
        map(response => searchDestinationPlacesSuccessAction({ response })),
        catchError(() => [
          searchDestinationPlacesSuccessAction({
            response: {
              hotels: [],
              locations: []
            }
          })
        ])
      )),
    ));

  searchExtraByQuery$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(searchExtraByQueryAction),
      switchMap(action => this.service.searchExtraByQuery(action.query).pipe(
        map(response => searchExtraByQuerySuccessAction({ response })),
        catchError(() => [
          searchExtraByQuerySuccessAction({
            response: {
              activities: [],
              destinations: []
            }
          })
        ])
      )),
    ));

  searchTravelExtraActivityInfo$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(searchTravelExtraActivityInfoAction),
      switchMap(action => iif(
        () => action.payload?.Type === ActivitySearchCodeType.Activity,
        this.service.searchTravelExtraActivityInfo(action.payload),
        of(null)
      ).pipe(
        switchMap(response => {
          if (action.payload?.Type === ActivitySearchCodeType.Activity) {
            return [
              selectTravelExtraInputAction({
                selection: {
                  Id: action.payload?.Code,
                  codeType: action.payload?.Type,
                  activityName: response?.data?.activityNameEs,
                  activityType: response?.data?.activityType,
                }
              })
            ];
          } else {
            return EMPTY;
          }
        }),
        catchError(() => {
          this.navigationService.navigateToBack();
          return [
            cleanActivitiesResultsAction()
          ];
        })
      )),
    ));

  searchDestinationPlaceInfo$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(searchDestinationPlaceInfoAction),
      switchMap(action => this.service.searchDestinationPlaceInfo(action.payload).pipe(
        switchMap(response => {
          if (!response?.NearestIata) {
            this.navigationService.navigateToBack();
            this.appFacade.fetchToastrMessage({ message: this.messageInputIataError, type: ToastrTypes.ERROR });
            return [
              clearHotelResultsAction()
            ];
          } else {
            return [
              searchDestinationPlaceInfoSuccessAction({ response }),
              searchPackageDestinationPlaceInfoSuccessAction({ response })
            ];
          }
        }),
        catchError(() => {
          this.baseSearchBarFacade.setTabSelectedHome(SearchType.HOME_HOTELS);
          this.navigationService.navigateToBack();
          return [
            clearHotelResultsAction()
          ];
        })
      ))
    ));

  private get messageInputIataError(): string {
    return this.translateService.translate('ERROR_REQUEST');
  }
}
